<template>
  <div class="employee-section-content">
    <!-- Legend Section -->
    <div class="legend-section">
      <div class="row">
        <div class="col">
          <span class="legend-key">M</span> = Mandatory
        </div>
        <div class="col">
          <span class="legend-key">D</span> = Development
        </div>
        <div class="col">
          <span class="legend-key">C</span> = Completed
        </div>
        <div class="col">
          <span class="legend-key">NC</span> = Not Completed
        </div>
        <div class="col">
          <span class="legend-key">ED</span> = Expired
        </div>
      </div>
    </div>

    <!-- Table Section -->
    <div class="table-section">
      <!-- Additional Filters -->
      <div class="additional-filters mb-3">
        <div class="row align-items-center">
          <!-- Search by Course Name (Only for Employee Role) -->
          <div class="col-12 col-md-4 mb-2">
            <label class="mb-1 font-weight-bold">Search Course:</label>
            <b-form-input
              v-model="filters.searchCourseName"
              placeholder="Search by course name..."
              class="filter-input"
            ></b-form-input>
          </div>

          <!-- Clear All Button -->
          <div class="col-12 col-md-2 mb-2 d-flex align-items-end">
            <b-button 
              variant="outline-secondary" 
              size="sm" 
              class="w-100"
              @click="clearAllFilters"
            >
              Clear All
            </b-button>
          </div>
        </div>
      </div>

      <div class="table-scroll-wrapper">
        <table class="table employee-table">
        <thead>
          <tr>
            <th class="text-center" style="vertical-align: middle;">NO</th>
            <th class="text-center" style="vertical-align: middle;">ROLE COMPETENCY</th>
            <th style="vertical-align: middle;">TRAINING</th>
            <th style="vertical-align: middle;">POSITION</th>
            <th class="text-center" style="vertical-align: middle;">PLAN</th>
            <th class="text-center" style="vertical-align: middle;">ACTUAL</th>
            <th style="vertical-align: middle;">COMPETENCY</th>
            <th class="text-center" style="vertical-align: middle;">PROFICIENCY LEVEL</th>
            <th class="text-center" style="vertical-align: middle;">PROFICIENCY STANDARD</th>
            <th class="text-center" style="vertical-align: middle;">GAP COMPETENCY</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(item, index) in paginatedTrainingData">
            <tr :key="index">
              <td class="text-center">{{ ((dashboardDetailEmployee?.pagination?.page || 1) - 1) * (dashboardDetailEmployee?.pagination?.limit || 10) + index + 1 }}</td>
              <td class="text-center">{{ item.roleCompetency }}</td>
              <td>{{ item.training }}</td>
              <td>{{ item.position }}</td>
              <td class="text-center">{{ item.plan }}</td>
              <td class="text-center">{{ item.actual }}</td>
              <td>{{ item.competency }}</td>
              <td class="text-center">{{ item.proficiencyLevel }}</td>
              <td class="text-center">{{ item.proficiencyStandard }}</td>
              <td class="text-center">{{ item.gapCompetency }}</td>
            </tr>
          </template>
        </tbody>
      </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-wrapper">
        <nav>
          <ul class="pagination pagination-sm mb-0">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">‹</a>
            </li>
            <li 
              v-for="page in visiblePages" 
              :key="page" 
              class="page-item" 
              :class="{ active: currentPage === page, disabled: page === '...' }"
            >
              <a 
                v-if="page === '...'" 
                class="page-link" 
                href="#"
                @click.prevent>
                ...
              </a>
              <a 
                v-else
                class="page-link" 
                href="#" 
                @click.prevent="changePage(page)">
                {{ page }}
              </a>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
              <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">›</a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import { hasRole, ROLE_EMPLOYEE, ROLE_MANAGER } from '@/constants/mitraRole';
import moment from 'moment';

export default {
  name: "EmployeeDetailSection",
  props: {
    companyName: {
      type: String,
      default: ""
    },
    companyData: {
      type: Object,
      default: null
    },
    kplhType: {
      type: String,
      default: "KPLH"
    },
    trainingType: {
      type: String,
      default: "all"
    },
    dateRange: {
      type: Object,
      default: () => ({ start: null, end: null })
    }
  },
  data() {
    return {
      currentPage: 1,
      perPage: 10,
      filters: {
        searchCourseName: ''
      },
      employeeList: []
    };
  },
  computed: {
    ...mapState('trainingCategories', ['trainingCategories']),
    ...mapGetters('users', ['profileData']),
    ...mapGetters('dashboard', ['dashboardDetailEmployee']),
    filteredTrainingData() {
      console.log('this.dashboardDetailEmployee?.data', this.dashboardDetailEmployee?.data);
      // Return data from API
      return this.dashboardDetailEmployee?.data || [];
    },
    paginatedTrainingData() {
      // Data already paginated from API
      return this.filteredTrainingData?.trainingData;
    },
    totalPages() {
      return this.dashboardDetailEmployee?.pagination?.totalPages || 1;
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      
      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        
        if (current > 3) {
          pages.push('...');
        }
        
        const start = Math.max(2, current - 1);
        const end = Math.min(total - 1, current + 1);
        
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
        
        if (current < total - 2) {
          pages.push('...');
        }
        
        pages.push(total);
      }
      
      return pages.length > 0 ? pages : [1];
    },
    isKPLH() {
      return this.kplhType === 'KPLH';
    },
    columnsPerCompetency() {
      return this.isKPLH ? 2 : 4;
    },
    competencyTypes() {
      return this.trainingCategories.map(category => ({
        code: category.abbreviation,
        name: category.title,
        id: category.id
      }));
    }
  },
  watch: {
    'filters.searchCourseName'() {
      // Reset to page 1 when searching
      this.currentPage = 1;
      this.fetchEmployeeDetail();
    },
    dateRange: {
      deep: true,
      handler() {
        this.currentPage = 1;
        this.fetchEmployeeDetail();
      }
    },
    kplhType() {
      this.currentPage = 1;
      this.fetchEmployeeDetail();
    },
    trainingType() {
      this.currentPage = 1;
      this.fetchEmployeeDetail();
    }
  },
  mounted() {
    this.fetchTrainingCategoriesData();
    this.fetchEmployeeDetail();
  },
  methods: {
    ...mapActions('trainingCategories', ['fetchTrainingCategories']),
    ...mapActions('dashboard', ['getDashboardDetailEmployee']),
    async fetchTrainingCategoriesData() {
      try {
        await this.fetchTrainingCategories({ limit: '', search: '' });
      } catch (error) {
        console.error('Error fetching training categories:', error);
      }
    },
    async fetchEmployeeDetail() {
      try {
        const params = {
          page: this.currentPage,
          limit: this.perPage,
          matrix_kplh: this.kplhType,
          training_type: this.trainingType || 'all'
        };

        if (this.dateRange?.start) {
          params.start_date = moment(this.dateRange.start).format('MM/DD/YYYY');
        }

        if (this.dateRange?.end) {
          params.end_date = moment(this.dateRange.end).format('MM/DD/YYYY');
        }
        
        // Add search parameter if exists
        if (this.filters.searchCourseName) {
          params.search = this.filters.searchCourseName;
        }
        
        await this.getDashboardDetailEmployee(params);
      } catch (error) {
        console.error('Error fetching employee detail:', error);
      }
    },
    clearAllFilters() {
      this.filters = {
        division: null,
        department: null,
        searchCourseName: ''
      };
      this.currentPage = 1;
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.fetchEmployeeDetail();
      }
    }
  }
};
</script>

<style scoped>
.employee-section-content {
  padding: 1.5rem;
}

/* Legend Section */
.legend-section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.legend-section .row {
  font-size: 0.875rem;
}

.legend-key {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background-color: #B71C1C;
  color: #fff;
  font-weight: 700;
  border-radius: 3px;
  margin-right: 0.5rem;
}

/* Table Section */
.table-section {
  margin-top: 1.5rem;
}

/* Competency Filter */
.competency-filter {
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.competency-filter label {
  font-size: 0.875rem;
  color: #333;
}

.competency-select {
  font-size: 0.875rem;
}

.table-scroll-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table-scroll-wrapper::-webkit-scrollbar {
  height: 8px;
}

.table-scroll-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-scroll-wrapper::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.table-scroll-wrapper::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.employee-table {
  width: 100%;
  margin-bottom: 1rem;
  font-size: 0.75rem;
  border-collapse: collapse;
}

.employee-table thead th {
  background-color: #fafafa;
  color: #9e9e9e;
  font-weight: 400;
  text-transform: uppercase;
  padding: 0.75rem 0.5rem;
  border: 1px solid #e0e0e0;
  font-size: 0.65rem;
  letter-spacing: 0.3px;
}

.employee-table tbody td {
  padding: 1rem 0.5rem;
  border: 1px solid #e0e0e0;
  color: #000;
  background-color: #fff;
  font-size: 0.75rem;
}

.employee-table tbody tr:hover {
  background-color: #f8f9fa;
}

/* Pagination */
.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
}

.pagination {
  display: flex;
  gap: 0;
  margin: 0;
  padding: 0;
  list-style: none;
}

.page-item {
  margin: 0;
}

.page-link {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  color: #333;
  background-color: #fff;
  border: 1px solid #dee2e6;
  text-decoration: none;
  margin: 0 2px;
  border-radius: 3px;
}

.page-item.active .page-link {
  background-color: #B71C1C;
  border-color: #B71C1C;
  color: #fff;
}

.page-item.disabled .page-link {
  color: #6c757d;
  pointer-events: none;
  background-color: #fff;
  border-color: #dee2e6;
}

.page-link:hover {
  color: #B71C1C;
  background-color: #f8f9fa;
  border-color: #dee2e6;
}

.page-item.active .page-link:hover {
  color: #fff;
  background-color: #B71C1C;
}
</style>
